/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useMemo } from 'react';

import {
  Typography,
  Backdrop,
  CircularProgress,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useGetMeMutation } from 'src/common/apis/CommonApi';

import { useGetEncounterSessionQuery } from '../../../common/apis/FlorenceApi';

import { SmartLaunchHeader } from './SmartLaunchHeader';
import SmartLaunchTabClinicNotes from './SmartLaunchTabClinicNotes';
import SmartLaunchTabSurgeryEngine from './SmartLaunchTabSurgeryEngine';

const SmartLaunchScreen: React.FC = () => {
  const { id } = useParams();
  const [getMeMutation, { isLoading: userIsFetching }] = useGetMeMutation();

  const { data: encounterSession, isFetching: encounterIsFetching } =
    useGetEncounterSessionQuery(id ?? '', {
      refetchOnMountOrArgChange: true,
    });

  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Run on page load
  React.useEffect(() => {
    getMeMutation();
  }, [getMeMutation]);

  const parsedEncounterSessionForSurgeryEngine = useMemo(() => {
    // Loop over the clinical docs and add them inside the scan text concatenated
    if (!encounterSession?.clinicalDocsExtractedText) {
      return null;
    }

    let scanText = '';
    const clinicalDocs = encounterSession.clinicalDocsExtractedText;

    // Loop through object entries instead of using numeric indices
    Object.entries(clinicalDocs).forEach(([key, text], index) => {
      scanText += `--- DOCUMENT ${index + 1}:\n`;
      scanText += text;
      scanText += '\n\n\n';
    });

    return {
      ...encounterSession,
      sheet: {
        ...encounterSession.sheet,
        scanText,
        sheetCodes: [],
      },
    };
  }, [encounterSession]);

  if (encounterIsFetching || userIsFetching) {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1300,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={true}
      >
        <CircularProgress color='secondary' size={64} />
      </Backdrop>
    );
  }

  if (!encounterSession || !id) {
    return (
      <Typography
        textAlign='center'
        marginTop='120px'
        fontSize={18}
        sx={{ padding: '20px' }}
      >
        The Encounter Session that you are trying to find does not exist or you
        do not have permission to access it. If this is an error, please contact
        an administrator.
      </Typography>
    );
  }

  return (
    <Box>
      <SmartLaunchHeader />

      <Box
        className='responsive-horizontal-padding'
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          marginTop: '84px',
        }}
      >
        {encounterSession.clinicalDocsExtractedText && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              marginLeft: '20px',
              marginRight: '20px',
            }}
          >
            <Tab label='Clinic Notes' />
            <Tab label='Surgery Notes' />
          </Tabs>
        )}
      </Box>

      <div style={{ display: activeTab === 0 ? 'block' : 'none' }}>
        <SmartLaunchTabClinicNotes initialEncounterSession={encounterSession} />
      </div>
      {parsedEncounterSessionForSurgeryEngine && (
        <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
          <SmartLaunchTabSurgeryEngine
            initialEncounterSession={parsedEncounterSessionForSurgeryEngine}
          />
        </div>
      )}
    </Box>
  );
};

export default SmartLaunchScreen;
